import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import ImageUpload from '../../components/admin/ImageUpload';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL, getUploadUrl } from '../../config/api';
import './css/AdminPages.css';

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    key_name: '',
    name_ar: '',
    name_en: '',
    image: '',
    display_order: 0,
    is_active: true
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, isRTL, toggleLanguage } = useLanguage();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');

    try {
      if (editingCategory) {
        await axios.put(
          `${API_URL}/api/categories/${editingCategory.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API_URL}/api/categories`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      fetchCategories();
      handleCloseModal();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error saving category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      key_name: category.key_name,
      name_ar: category.name_ar,
      name_en: category.name_en,
      image: category.image || '',
      display_order: category.display_order,
      is_active: category.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.deleteConfirm'))) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/categories/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || t('admin.error') || 'Error deleting category');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      key_name: '',
      name_ar: '',
      name_en: '',
      image: '',
      display_order: 0,
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>{t('admin.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`admin-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="admin-header">
        <button onClick={() => navigate('/admin/dashboard')} className="back-btn">
          <ArrowLeftIcon className="icon" />
          {t('admin.back')}
        </button>
        <h1>{t('admin.categories')}</h1>
        <div className="header-actions">
          <button onClick={toggleLanguage} className="language-toggle-btn">
            {language === 'ar' ? 'EN' : 'AR'}
          </button>
          <button onClick={() => setShowModal(true)} className="add-btn">
            + {t('admin.add')} {t('admin.categories')}
          </button>
        </div>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.id')}</th>
              <th>{t('admin.key')}</th>
              <th>{t('admin.nameAr')}</th>
              <th>{t('admin.nameEn')}</th>
              <th>{t('admin.image')}</th>
              <th>{t('admin.order')}</th>
              <th>{t('admin.active')}</th>
              <th>{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.id}>
                <td>{cat.id}</td>
                <td>{cat.key_name}</td>
                <td>{cat.name_ar}</td>
                <td>{cat.name_en}</td>
                <td>
                  {cat.image && (
                    <img src={getUploadUrl(cat.image)} alt={cat.name_ar} className="table-image" />
                  )}
                </td>
                <td>{cat.display_order}</td>
                <td>{cat.is_active ? t('admin.yes') : t('admin.no')}</td>
                <td>
                  <button onClick={() => handleEdit(cat)} className="edit-btn">{t('admin.edit')}</button>
                  <button onClick={() => handleDelete(cat.id)} className="delete-btn">{t('admin.delete')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCategory ? t('admin.editCategory') : t('admin.addCategory')}</h2>
            <form onSubmit={handleSubmit}>
              {!editingCategory && (
                <div className="form-group">
                  <label>{t('admin.keyName')} *</label>
                  <input
                    type="text"
                    value={formData.key_name}
                    onChange={(e) => setFormData({ ...formData, key_name: e.target.value })}
                    required
                    placeholder="e.g., baits"
                  />
                </div>
              )}
              <div className="form-group">
                <label>{t('admin.nameAr')} *</label>
                <input
                  type="text"
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.nameEn')} *</label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.image')}</label>
                <ImageUpload
                  onUploadComplete={(filename) => setFormData({ ...formData, image: filename })}
                  currentImage={formData.image ? `/uploads/${formData.image}` : null}
                />
              </div>
              <div className="form-group">
                <label>{t('admin.displayOrder')}</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  {t('admin.active')}
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={handleCloseModal} className="cancel-btn">{t('admin.cancel')}</button>
                <button type="submit" className="save-btn">{t('admin.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
